import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    Switch,
    FormControlLabel,
    Typography,
    Box,
    Chip,
    IconButton,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { PhotoCamera } from "@mui/icons-material";
import { useSelector, useDispatch } from "react-redux";
import { generateId } from "common/src/helper/helper";
import { addNewCar, updateUserCarImage } from "common/src/actions/caractions";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { firebase } from "common/src/config/configureFirebase";
import { t } from "i18next";
import { api } from "common";

const ImageUploadBox = styled(Box)(({ theme }) => ({
    width: "100%",
    height: 200,
    border: `2px dashed ${theme.palette.primary.main}`,
    borderRadius: theme.shape.borderRadius,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    marginBottom: theme.spacing(2),
    position: "relative",
    "&:hover": {
        backgroundColor: theme.palette.action.hover,
    },
}));

const ImagePreview = styled("img")({
    width: "100%",
    height: "100%",
    objectFit: "cover",
    borderRadius: 8,
});

const CategoryChip = styled(Chip)(({ theme, selected }) => ({
    margin: theme.spacing(0.5),
    backgroundColor: selected ? theme.palette.primary.main : "transparent",
    color: selected
        ? theme.palette.primary.contrastText
        : theme.palette.text.primary,
    "&:hover": {
        backgroundColor: selected
            ? theme.palette.primary.dark
            : theme.palette.action.hover,
    },
}));

const AddCarDialog = ({ open, onClose }) => {
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);
    const [selectedDriver, setSelectedDriver] = useState("");
    const [drivers, setDrivers] = useState([]);
    const [carImages, setCarImages] = useState([
        { image: null, label: "car_front" },
        { image: null, label: "car_driver_side" },
        { image: null, label: "car_back" },
        { image: null, label: "car_passenger_side" },
    ]);
    const [selectedImageIndex, setSelectedImageIndex] = useState(0);
    const [optionCars, setOptionCars] = useState([]);
    const [hasChangeCategory, setHasChangeCategory] = useState(false);

    const auth = useSelector((state) => state.auth);
    const cartypes = useSelector((state) => state.cartypes.cars);
    const categorylistdata = useSelector((state) => state.categorylistdata);
    const settings = useSelector((state) => state.settingsdata.settings);
    const featureFlags = useSelector((state) => state.featureflagsdata.data);
    const addons = useSelector((state) => state.addondata.data);
    const users = useSelector((state) => state.usersdata.users); // Use users like CarsList.js
    const [isMultiService, setIsMultiService] = useState(false);

    const [formData, setFormData] = useState({
        vehicleMake: "",
        vehicleModel: "",
        vehicleNumber: "",
        other_info: "",
    });

    const resetAllData = () => {
        setSelectedDriver("");
        setCarImages([
            { image: null, label: "car_front" },
            { image: null, label: "car_driver_side" },
            { image: null, label: "car_back" },
            { image: null, label: "car_passenger_side" },
        ]);
        setSelectedImageIndex(0);
        setHasChangeCategory(false);
        setFormData({
            vehicleMake: "",
            vehicleModel: "",
            vehicleNumber: "",
            other_info: "",
        });
        // Reset categories and types to initial state
        if (
            categorylistdata.categories &&
            categorylistdata.categories.length >= 1 &&
            cartypes &&
            cartypes.length >= 1
        ) {
            const categories = categorylistdata.categories
                .filter((item) => item.active)
                .map((category) => {
                    const categoryTypes = cartypes.filter(
                        (type) =>
                            type.category === category.category &&
                            type.active
                    );
                    return {
                        ...category,
                        types: categoryTypes.map((type) => ({
                            ...type,
                            selected: false,
                        })),
                        selected: false,
                    };
                })
                .filter((category) => {
                    const hasMultipleTypes = category.types.length >= 1;
                    console.log(
                        `[resetAllData] Category ${category.category}: ${category.types.length} active types, will ${hasMultipleTypes ? "SHOW" : "HIDE"}`
                    );
                    return hasMultipleTypes;
                }); // Only show categories with at least 1 active cartype

            // Don't auto-select any category, let admin choose
            setOptionCars(categories);
        }
    };

    const handleClose = () => {
        resetAllData();
        onClose();
    };

    useEffect(() => {
        if (
            featureFlags &&
            featureFlags.add_on &&
            addons &&
            addons?.multi_service &&
            settings
        ) {
            setIsMultiService(true);
        } else {
            setIsMultiService(false);
        }
    }, [featureFlags, addons, settings]);

    // Fetch users when modal opens
    useEffect(() => {
        if (open) {
            dispatch(api.fetchUsersOnce());
        }
    }, [open, dispatch]);

    useEffect(() => {
        // Filter users to get only drivers
        if (users && users.length > 0) {
            // Filter users based on role like CarsList.js
            const filteredUsers = users.filter(
                (user) =>
                    user.usertype === "driver" &&
                    ((auth.profile.usertype === "fleetadmin" &&
                        user.fleetadmin &&
                        user.fleetadmin === auth.profile.uid) ||
                        auth.profile.usertype === "admin")
            );

            const driverList = filteredUsers
                .filter((user) => user.id) // Use id as driver ID, not uid
                .map((driver) => ({
                    id: driver.id, // Use id as unique ID, not uid
                    name:
                        `${driver.firstName || ""} ${
                            driver.lastName || ""
                        }`.trim() || "Unknown Driver",
                    email: driver.email || "",
                }))
                .filter((driver, index, self) => {
                    // Remove duplicates and ensure ID is not empty
                    const isValid =
                        driver.id &&
                        driver.id !== "" &&
                        self.findIndex((d) => d.id === driver.id) === index;
                    return isValid;
                });
            console.log("Processed drivers:", driverList);
            console.log(
                "Driver IDs (id):",
                driverList.map((d) => d.id)
            );
            setDrivers(driverList);
        } else {
            setDrivers([]);
        }
    }, [users, auth.profile]);

    // Initialize categories and car types when modal opens or data changes
    useEffect(() => {
        if (open) {
            console.log(
                "Modal opened, checking for categories and car types..."
            );
            console.log("categorylistdata:", categorylistdata);
            console.log("cartypes:", cartypes);
            console.log(
                "categorylistdata.categories:",
                categorylistdata.categories
            );
            console.log(
                "cartypes length:",
                cartypes ? cartypes.length : "undefined"
            );

            // Check if we have the required data - use >= 1 instead of truthy check
            if (
                categorylistdata.categories &&
                categorylistdata.categories.length >= 1 &&
                cartypes &&
                cartypes.length >= 1
            ) {
                console.log("Initializing categories and car types...");

                const activeCategories = categorylistdata.categories.filter(
                    (item) => item.active
                );
                console.log("Active categories:", activeCategories);

                const categories = activeCategories
                    .map((category) => {
                        const categoryTypes = cartypes.filter(
                            (type) =>
                                type.category === category.category &&
                                type.active
                        );
                        console.log(
                            `Types for category ${category.category}:`,
                            categoryTypes,
                            `Count: ${categoryTypes.length}`
                        );

                        return {
                            ...category,
                            types: categoryTypes.map((type) => ({
                                ...type,
                                selected: false,
                            })),
                            selected: false,
                        };
                    })
                    .filter((category) => {
                        const hasMultipleTypes = category.types.length >= 1;
                        console.log(
                            `Category ${category.category}: ${category.types.length} active types, will ${hasMultipleTypes ? "SHOW" : "HIDE"}`
                        );
                        return hasMultipleTypes;
                    }); // Only show categories with at least 1 active cartype

                console.log(
                    "Final processed categories for modal:",
                    categories,
                    `Total: ${categories.length} categories`
                );
                setOptionCars(categories);
            } else {
                console.warn(
                    "Categories or car types not available yet. Will retry when data is loaded."
                );
                console.warn(
                    "categorylistdata.categories available:",
                    !!categorylistdata.categories
                );
                console.warn("cartypes available:", !!cartypes);
                // Set empty array to prevent undefined errors
                setOptionCars([]);
            }
        }
    }, [open, categorylistdata.categories, cartypes]);

    const handleImageUpload = async (event) => {
        const file = event.target.files[0];
        if (file) {
            // Validate file type
            if (!file.type.startsWith("image/")) {
                alert(t("please_select_an_image_file"));
                return;
            }

            // Validate file size (e.g., max 5MB)
            if (file.size > 5 * 1024 * 1024) {
                alert(t("image_size_should_be_less_than_5mb"));
                return;
            }

            const newCarImages = [...carImages];
            newCarImages[selectedImageIndex] = {
                ...newCarImages[selectedImageIndex],
                image: file,
            };
            setCarImages(newCarImages);
        }
    };

    const handleImageClick = (index) => {
        setSelectedImageIndex(index);
        // Trigger file input click
        document.getElementById(`image-upload-${index}`).click();
    };

    const handleToggleOption = (index, value) => {
        const newOptions = [...optionCars];

        // For single service mode: only allow one category to be selected
        if (!isMultiService && value === true) {
            // Deselect all other categories
            newOptions.forEach((option, i) => {
                if (i !== index) {
                    option.selected = false;
                    option.types.forEach((type) => {
                        type.selected = false;
                    });
                }
            });
        }

        newOptions[index].selected = value;

        // Allow multiple categories to be selected only in multi-service mode
        if (value === true) {
            const existingTypes = newOptions[index].types.filter(
                (type) => type.selected
            );

            if (
                existingTypes.length === 0 &&
                newOptions[index].types.length > 0
            ) {
                // In single service mode, only select the first type
                if (!isMultiService) {
                    newOptions[index].types[0].selected = true;
                    // Ensure no other types are selected in single service mode
                    for (let i = 1; i < newOptions[index].types.length; i++) {
                        newOptions[index].types[i].selected = false;
                    }
                } else {
                    newOptions[index].types[0].selected = true;
                }
            }
        }

        // If unchecking, also uncheck all child types
        if (value === false) {
            newOptions[index].types.forEach((type) => {
                type.selected = false;
            });
        }

        setHasChangeCategory(true);
        setOptionCars(newOptions);
    };

    const handleToggleChildOption = (parentIndex, childIndex, value) => {
        const parentSelected = optionCars[parentIndex].selected;
        if (!parentSelected) {
            alert(t("please_select_category"));
            return;
        }

        const isChildSelected =
            optionCars[parentIndex].types[childIndex].selected;
        const isChildNotSelected =
            optionCars[parentIndex].types.filter((type) => type.selected)
                .length === 1;

        if (isChildSelected && value === false && isChildNotSelected) {
            alert(t("at_least_one_type_selected"));
            return;
        }

        const newOptions = [...optionCars];

        // For single service mode: only allow one type to be selected
        if (!isMultiService && value === true) {
            // Deselect all other types in this category
            newOptions[parentIndex].types.forEach((type, i) => {
                if (i !== childIndex) {
                    type.selected = false;
                }
            });

            // Also deselect all types in other categories
            newOptions.forEach((option, i) => {
                if (i !== parentIndex) {
                    option.selected = false;
                    option.types.forEach((type) => {
                        type.selected = false;
                    });
                }
            });
        }

        newOptions[parentIndex].types[childIndex].selected = value;

        setHasChangeCategory(true);
        setOptionCars(newOptions);
    };

    const uploadFile = async (file, storagePath) => {
        try {
            const storage = getStorage();
            const storageRef = ref(storage, storagePath);
            await uploadBytes(storageRef, file);
            return await getDownloadURL(storageRef);
        } catch (error) {
            throw error;
        }
    };

    const handleSubmit = async () => {
        // Check for empty images
        const missingImages = carImages.filter((image) => !image.image);
        if (missingImages.length > 0) {
            alert(
                t("please_add_images_for") +
                    missingImages.map((img) => t(img.label)).join(", ")
            );
            return;
        }

        const carId = generateId();

        // Check for selected cars
        const selectedCars = optionCars
            .filter((category) => category.selected)
            .flatMap((category) =>
                category.types
                    .filter((type) => type.selected)
                    .map((type) => ({
                        carType: type.name,
                        category: category.category,
                        approved: false,
                        active: false,
                        car_id: carId,
                        id: generateId(),
                    }))
            );

        if (selectedCars.length === 0) {
            alert(t("please_select_at_least_one_car_type"));
            return;
        }

        // Check for required form fields
        if (
            !formData.vehicleMake ||
            !formData.vehicleModel ||
            !formData.vehicleNumber
        ) {
            alert(t("please_fill_in_all_required_fields"));
            return;
        }

        // Check if driver is selected
        if (!selectedDriver || selectedDriver === "") {
            alert(t("please_select_a_driver"));
            return;
        }

        setLoading(true);
        try {
            const newCarImages = await Promise.all(
                carImages.map(async (image, index) => {
                    if (image.image) {
                        const storagePath = `cars/${carId}_${image.label}`;
                        const downloadURL = await uploadFile(
                            image.image,
                            storagePath
                        );
                        return { ...image, image: downloadURL };
                    }
                    return image;
                })
            );

            // Ambil semua kategori dan carType unik dari selectedCars
            const categories = [
                ...new Set(selectedCars.map((c) => c.category)),
            ].join(",");
            const carTypes = [
                ...new Set(selectedCars.map((c) => c.carType)),
            ].join(",");

            const carData = {
                active: false,
                enabled: true,
                selected: true,
                for_multi_service: isMultiService,
                createdAt: Date.now(),
                cars: selectedCars,
                car_images: newCarImages,
                driver: selectedDriver, // selectedDriver is now the driver's id (not uid)
                id: carId,
                ...formData,
                category: categories,
                carType: carTypes,
            };

            console.log("Car data to be saved:", carData);
            console.log("Selected driver ID:", selectedDriver);

            dispatch(
                addNewCar(
                    carData,
                    () => {
                        setLoading(false);
                        onClose();
                    },
                    () => {
                        setLoading(false);
                        alert(t("error_adding_car_please_try_again"));
                    }
                )
            );
        } catch (error) {
            setLoading(false);
            // console.error('Error adding car:', error);
            alert(t("error_adding_car_please_try_again"));
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle>{t("add_new_car")}</DialogTitle>
            <DialogContent>
                <Grid container spacing={3}>
                    {/* Driver Selection */}
                    <Grid item xs={12}>
                        <FormControl
                            fullWidth
                            variant="outlined"
                            sx={{ mt: 2, mb: 2 }}
                        >
                            <InputLabel
                                id="driver-select-label"
                                shrink={Boolean(selectedDriver)}
                                sx={{
                                    backgroundColor: "white",
                                    padding: "0 8px",
                                    zIndex: 1,
                                    lineHeight: "1.2",
                                    fontSize: "0.875rem",
                                    "&.Mui-focused": {
                                        backgroundColor: "white",
                                    },
                                }}
                            >
                                {t("select_driver")}
                            </InputLabel>
                            <Select
                                labelId="driver-select-label"
                                id="driver-select"
                                value={selectedDriver || ""}
                                label={t("select_driver")}
                                onChange={(e) =>
                                    setSelectedDriver(e.target.value)
                                }
                                sx={{
                                    "& .MuiSelect-select": {
                                        paddingTop: "16px",
                                        paddingBottom: "16px",
                                    },
                                }}
                            >
                                <MenuItem value="" disabled>
                                    <em>{t("select_driver")}</em>
                                </MenuItem>
                                {drivers.map((driver) => (
                                    <MenuItem key={driver.id} value={driver.id}>
                                        {driver.name} ({driver.email})
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* Car Images */}
                    <Grid item xs={12}>
                        <Grid container spacing={2}>
                            {carImages.map((image, index) => (
                                <Grid item xs={6} key={index}>
                                    <ImageUploadBox
                                        onClick={() => handleImageClick(index)}
                                        sx={{
                                            borderColor:
                                                selectedImageIndex === index
                                                    ? "primary.main"
                                                    : "grey.300",
                                        }}
                                    >
                                        {image.image ? (
                                            <>
                                                <ImagePreview
                                                    src={URL.createObjectURL(
                                                        image.image
                                                    )}
                                                    alt={image.label}
                                                />
                                                <Box
                                                    sx={{
                                                        position: "absolute",
                                                        top: 8,
                                                        right: 8,
                                                        backgroundColor:
                                                            "rgba(0, 0, 0, 0.5)",
                                                        borderRadius: "50%",
                                                        padding: "4px",
                                                        cursor: "pointer",
                                                        "&:hover": {
                                                            backgroundColor:
                                                                "rgba(0, 0, 0, 0.7)",
                                                        },
                                                    }}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        const newCarImages = [
                                                            ...carImages,
                                                        ];
                                                        newCarImages[index] = {
                                                            ...newCarImages[
                                                                index
                                                            ],
                                                            image: null,
                                                        };
                                                        setCarImages(
                                                            newCarImages
                                                        );
                                                    }}
                                                >
                                                    <IconButton
                                                        size="small"
                                                        sx={{ color: "white" }}
                                                    >
                                                        <PhotoCamera />
                                                    </IconButton>
                                                </Box>
                                            </>
                                        ) : (
                                            <>
                                                <PhotoCamera />
                                                <Typography
                                                    variant="body2"
                                                    sx={{ mt: 1 }}
                                                >
                                                    {t(image.label)}
                                                </Typography>
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        mt: 1,
                                                        color: "text.secondary",
                                                    }}
                                                >
                                                    Click to upload image
                                                </Typography>
                                            </>
                                        )}
                                        <input
                                            type="file"
                                            accept="image/*"
                                            style={{ display: "none" }}
                                            onChange={handleImageUpload}
                                            id={`image-upload-${index}`}
                                        />
                                    </ImageUploadBox>
                                </Grid>
                            ))}
                        </Grid>
                    </Grid>

                    {/* Categories and Types */}
                    <Grid item xs={12}>
                        <Typography variant="h6" sx={{ mb: 2 }}>
                            {isMultiService
                                ? t("select_categories_and_services")
                                : t("select_single_service")}
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{ mb: 2, color: "text.secondary" }}
                        >
                            {isMultiService
                                ? t(
                                      "you_can_select_multiple_categories_and_services"
                                  )
                                : t("you_can_select_only_one_service")}
                        </Typography>

                        {/* Loading state for categories */}
                        {!categorylistdata.categories ||
                        categorylistdata.categories.length < 1 ||
                        !cartypes ||
                        cartypes.length < 1 ? (
                            <Box sx={{ p: 2, textAlign: "center" }}>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    Loading categories and car types...
                                </Typography>
                            </Box>
                        ) : optionCars.length === 0 ? (
                            <Box sx={{ p: 2, textAlign: "center" }}>
                                <Typography variant="body2" color="error">
                                    No categories available. Please check if
                                    categories and car types are configured.
                                </Typography>
                            </Box>
                        ) : (
                            optionCars.map((category, index) => (
                                <Box key={index} sx={{ mb: 2 }}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={category.selected}
                                                onChange={(e) =>
                                                    handleToggleOption(
                                                        index,
                                                        e.target.checked
                                                    )
                                                }
                                            />
                                        }
                                        label={category.category}
                                    />
                                    <Box
                                        sx={{
                                            display: "flex",
                                            flexWrap: "wrap",
                                            mt: 1,
                                        }}
                                    >
                                        {category.types.map(
                                            (type, typeIndex) => (
                                                <CategoryChip
                                                    key={typeIndex}
                                                    label={type.name}
                                                    selected={type.selected}
                                                    onClick={() =>
                                                        handleToggleChildOption(
                                                            index,
                                                            typeIndex,
                                                            !type.selected
                                                        )
                                                    }
                                                    disabled={
                                                        !category.selected
                                                    }
                                                />
                                            )
                                        )}
                                    </Box>
                                </Box>
                            ))
                        )}
                    </Grid>

                    {/* Car Details */}
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label={t("vehicle_model_name")}
                            value={formData.vehicleMake}
                            onChange={(e) =>
                                setFormData({
                                    ...formData,
                                    vehicleMake: e.target.value,
                                })
                            }
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label={t("vehicle_model_no")}
                            value={formData.vehicleModel}
                            onChange={(e) =>
                                setFormData({
                                    ...formData,
                                    vehicleModel: e.target.value,
                                })
                            }
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label={t("vehicle_no")}
                            value={formData.vehicleNumber}
                            onChange={(e) =>
                                setFormData({
                                    ...formData,
                                    vehicleNumber: e.target.value,
                                })
                            }
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label={t("other_info")}
                            multiline
                            rows={3}
                            value={formData.other_info}
                            onChange={(e) =>
                                setFormData({
                                    ...formData,
                                    other_info: e.target.value,
                                })
                            }
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Cancel</Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={loading || !selectedDriver}
                >
                    {loading ? t("adding") : t("add_car")}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AddCarDialog;
